<template>
  <div v-if="hasAccess" class="meetings-page">
    <page-content class="meetings-page__content">
      <section class="meetings-panel">
        <header class="meetings-header">
          <div class="meetings-header__main">
            <router-link :to="{ name: 'app-meetings' }" class="meetings-back">
              <i class="fas fa-arrow-left" aria-hidden="true"></i> Réunions CSE
            </router-link>
            <h2 class="meetings-header__title">Déposer une réunion</h2>
            <p class="meetings-header__subtitle">
              L’enregistrement est transcrit puis résumé ; des notes sont résumées directement.
              Le traitement prend quelques minutes et se poursuit même si vous quittez la page.
            </p>
          </div>
        </header>

        <form id="meeting-form" class="meeting-form" novalidate @submit.prevent="submit">
          <section class="meeting-form__section">
            <h3 class="meetings-section-title">Type d’entrée</h3>
            <div class="meeting-form__modes" role="radiogroup" aria-label="Type d’entrée">
              <button
                v-for="mode in modes"
                :key="mode.key"
                type="button"
                role="radio"
                class="meeting-mode"
                :class="{ 'meeting-mode--active': inputType === mode.key }"
                :aria-checked="String(inputType === mode.key)"
                :disabled="submitting"
                @click="selectMode(mode.key)"
              >
                <span class="meeting-mode__icon"><i :class="mode.icon" aria-hidden="true"></i></span>
                <span class="meeting-mode__copy">
                  <span class="meeting-mode__label">{{ mode.label }}</span>
                  <span class="meeting-mode__hint">{{ mode.hint }}</span>
                </span>
              </button>
            </div>
          </section>

          <section v-if="inputType === 'audio'" class="meeting-form__section">
            <h3 class="meetings-section-title">Enregistrement</h3>
            <div
              class="meeting-dropzone"
              :class="{
                'meeting-dropzone--dragging': dragging,
                'meeting-dropzone--filled': !!file,
                'meeting-dropzone--error': !!fieldErrors.file
              }"
              @dragenter.prevent="onDragEnter"
              @dragover.prevent="onDragEnter"
              @dragleave.prevent="onDragLeave"
              @drop.prevent="onDrop"
            >
              <input
                ref="fileInput"
                type="file"
                class="sr-only"
                :accept="accept"
                :disabled="submitting"
                @change="onFileSelected"
              >
              <template v-if="file">
                <i class="far fa-file-audio meeting-dropzone__icon" aria-hidden="true"></i>
                <p class="meeting-dropzone__name">{{ file.name }}</p>
                <p class="meeting-dropzone__hint">{{ formatFileSize(file.size) }}</p>
                <p class="meeting-dropzone__actions">
                  <button type="button" class="meetings-link-button" :disabled="submitting" @click="openFilePicker">Changer de fichier</button>
                  <span aria-hidden="true">·</span>
                  <button type="button" class="meetings-link-button" :disabled="submitting" @click="clearFile">Retirer</button>
                </p>
              </template>
              <template v-else>
                <i class="fas fa-cloud-upload-alt meeting-dropzone__icon" aria-hidden="true"></i>
                <p class="meeting-dropzone__name">Glissez-déposez l’enregistrement ici</p>
                <p class="meeting-dropzone__hint">
                  ou <button type="button" class="meetings-link-button" :disabled="submitting" @click="openFilePicker">choisissez un fichier</button>
                </p>
                <p class="meeting-dropzone__hint">mp3, m4a, wav, webm, ogg, flac… jusqu’à 1 Go</p>
              </template>
            </div>
            <p v-if="fieldErrors.file" class="meeting-form__error">{{ fieldErrors.file.join(' ') }}</p>
          </section>

          <section v-else class="meeting-form__section">
            <label class="meetings-section-title" for="meeting-notes">Notes de la réunion</label>
            <textarea
              id="meeting-notes"
              v-model="notesText"
              rows="12"
              class="meeting-input meeting-input--textarea"
              :class="{ 'meeting-input--error': fieldErrors.notes_text }"
              :disabled="submitting"
              placeholder="Collez ou saisissez vos notes de séance : ordre du jour, échanges, décisions, points à suivre…"
            ></textarea>
            <p class="meeting-form__help" :class="{ 'meeting-form__help--warning': notesText.length && notesText.trim().length < 20 }">
              {{ notesText.length }} caractère{{ notesText.length > 1 ? 's' : '' }} · 20 minimum
            </p>
            <p v-if="fieldErrors.notes_text" class="meeting-form__error">{{ fieldErrors.notes_text.join(' ') }}</p>
          </section>

          <div class="meeting-form__grid">
            <section class="meeting-form__section">
              <label class="meetings-section-title" for="meeting-title">Titre <span class="meeting-form__optional">(facultatif)</span></label>
              <input
                id="meeting-title"
                v-model="title"
                type="text"
                maxlength="255"
                class="meeting-input"
                :class="{ 'meeting-input--error': fieldErrors.title }"
                :disabled="submitting"
                placeholder="Ex. : Réunion CSE d’octobre"
              >
              <p v-if="fieldErrors.title" class="meeting-form__error">{{ fieldErrors.title.join(' ') }}</p>
              <p v-else class="meeting-form__help">Sans titre, la date de réunion ou le nom du fichier est utilisé.</p>
            </section>
            <section class="meeting-form__section">
              <label class="meetings-section-title" for="meeting-date">Date de la réunion <span class="meeting-form__optional">(facultatif)</span></label>
              <t-datepicker
                id="meeting-date"
                v-model="meetingDate"
                :clearable="true"
                :disabled="submitting"
                placeholder="Sélectionnez la date…"
              />
              <p v-if="fieldErrors.meeting_date" class="meeting-form__error">{{ fieldErrors.meeting_date.join(' ') }}</p>
            </section>
          </div>

          <div v-if="generalErrors.length" class="meeting-form__alert" role="alert">
            <ul>
              <li v-for="(message, index) in generalErrors" :key="index">{{ message }}</li>
            </ul>
          </div>

          <div v-if="uploadProgress !== null" class="meeting-progress" aria-live="polite">
            <div class="meeting-progress__bar">
              <div class="meeting-progress__value" :style="{ width: `${uploadProgress}%` }"></div>
            </div>
            <span>{{ uploadProgressLabel }}</span>
          </div>
        </form>

        <footer class="meeting-form__footer">
          <button type="button" class="meeting-form__cancel" :disabled="submitting" @click="goBack">Annuler</button>
          <button type="submit" form="meeting-form" class="meeting-form__submit" :disabled="submitting || !canSubmit">
            <i v-if="submitting" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
            {{ submitting ? 'Envoi…' : 'Lancer le traitement' }}
          </button>
        </footer>
      </section>
    </page-content>
  </div>

  <div v-else class="meetings-no-access">
    <p>Vous n’avez pas accès à cette section.</p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { format } from 'date-fns'
import PageContent from '@/components/PageContent.vue'
import aiService from '@/services/ai.service'
import {
  MEETING_ROLES,
  AUDIO_EXTENSIONS,
  MAX_UPLOAD_BYTES,
  fileExtension,
  formatFileSize,
  validationErrors
} from './meetingHelpers'

const MODES = [
  {
    key: 'audio',
    icon: 'fas fa-microphone',
    label: 'Enregistrement audio',
    hint: 'Transcrit mot à mot, puis résumé'
  },
  {
    key: 'text',
    icon: 'fas fa-align-left',
    label: 'Notes écrites',
    hint: 'Vos notes de séance, résumées directement'
  }
]

const KNOWN_FIELDS = ['file', 'notes_text', 'title', 'meeting_date', 'input_type']

/**
 * Dépôt d'une réunion CSE : un enregistrement audio (glisser-déposer ou
 * sélection) ou des notes, avec un titre et une date facultatifs. Une fois
 * accepté par l'API (202), on rejoint la fiche, qui suit le traitement.
 */
export default {
  name: 'app-meetings-create',
  components: { PageContent },
  data () {
    return {
      modes: MODES,
      inputType: 'audio',
      file: null,
      notesText: '',
      title: '',
      meetingDate: null,
      dragging: false,
      dragDepth: 0,
      submitting: false,
      uploadProgress: null,
      fieldErrors: {},
      generalErrors: []
    }
  },
  computed: {
    ...mapGetters(['roles', 'domain']),
    hasAccess () {
      return this.roles.some(role => MEETING_ROLES.includes(role))
    },
    accept () {
      return AUDIO_EXTENSIONS.map(extension => `.${extension}`).concat('audio/*').join(',')
    },
    canSubmit () {
      if (this.inputType === 'audio') {
        return !!this.file && !this.fieldErrors.file
      }
      return this.notesText.trim().length >= 20
    },
    uploadProgressLabel () {
      if (this.uploadProgress === null) {
        return ''
      }
      return this.uploadProgress >= 100
        ? 'Enregistrement reçu, mise en file du traitement…'
        : `Envoi de l’enregistrement… ${this.uploadProgress} %`
    }
  },
  watch: {
    'domain.uuid' () {
      // Le dépôt vise le domaine de l'en-tête : changer de domaine en cours de
      // saisie ramène à la liste de ce nouveau domaine.
      this.$router.replace({ name: 'app-meetings' }).catch(() => {})
    }
  },
  methods: {
    formatFileSize,
    selectMode (mode) {
      if (mode === this.inputType) {
        return
      }
      this.inputType = mode
      this.fieldErrors = {}
      this.generalErrors = []
    },
    openFilePicker () {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click()
      }
    },
    onFileSelected (event) {
      const files = event.target.files
      this.takeFile(files && files[0] ? files[0] : null)
      // Permet de resélectionner le même fichier après un « Retirer ».
      event.target.value = ''
    },
    onDragEnter () {
      if (this.submitting) {
        return
      }
      this.dragDepth += 1
      this.dragging = true
    },
    onDragLeave () {
      this.dragDepth = Math.max(0, this.dragDepth - 1)
      if (!this.dragDepth) {
        this.dragging = false
      }
    },
    onDrop (event) {
      this.dragDepth = 0
      this.dragging = false
      if (this.submitting) {
        return
      }
      const files = event.dataTransfer ? event.dataTransfer.files : null
      this.takeFile(files && files[0] ? files[0] : null)
    },
    takeFile (file) {
      this.generalErrors = []
      this.fieldErrors = { ...this.fieldErrors, file: undefined }
      if (!file) {
        return
      }
      const errors = []
      if (!AUDIO_EXTENSIONS.includes(fileExtension(file.name))) {
        errors.push('Format audio non reconnu (mp3, m4a, wav, webm, ogg, flac…).')
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        errors.push(`L’enregistrement dépasse la taille maximale autorisée (1 Go, fichier de ${formatFileSize(file.size)}).`)
      }
      this.file = file
      this.fieldErrors = { ...this.fieldErrors, file: errors.length ? errors : undefined }
    },
    clearFile () {
      this.file = null
      this.fieldErrors = { ...this.fieldErrors, file: undefined }
    },
    asDate (value) {
      if (!value) {
        return null
      }
      const date = value instanceof Date ? value : new Date(value)
      return isNaN(date.getTime()) ? null : format(date, 'yyyy-MM-dd')
    },
    buildFormData ({ uploadId = null } = {}) {
      const formData = new FormData()
      formData.append('input_type', this.inputType)
      if (this.title.trim()) {
        formData.append('title', this.title.trim())
      }
      const meetingDate = this.asDate(this.meetingDate)
      if (meetingDate) {
        formData.append('meeting_date', meetingDate)
      }
      if (this.inputType === 'audio') {
        formData.append('upload_id', uploadId)
      } else {
        formData.append('notes_text', this.notesText)
      }
      return formData
    },
    // L'enregistrement part par morceaux : Cloudflare, devant l'API, refuse toute
    // requête de plus de 100 Mo. L'API décrit le découpage, chaque morceau est
    // envoyé à son tour (avec une seconde chance en cas de coupure) et la barre
    // suit les octets réellement partis. Rend l'upload_id à donner au dépôt.
    async uploadInChunks (file) {
      const { upload_id: uploadId, chunk_size: chunkSize, chunk_count: chunkCount } = await aiService.startMeetingUpload({
        name: file.name,
        size: file.size,
        mimeType: file.type
      })
      let sent = 0
      for (let index = 0; index < chunkCount; index++) {
        const blob = file.slice(index * chunkSize, Math.min(file.size, (index + 1) * chunkSize))
        await this.uploadChunk(uploadId, index, blob, loaded => this.setUploadProgress(sent + loaded, file.size))
        sent += blob.size
        this.setUploadProgress(sent, file.size)
      }
      return uploadId
    },
    uploadChunk (uploadId, index, blob, onLoaded, attempt = 1) {
      return aiService.uploadMeetingChunk(uploadId, index, blob, {
        onUploadProgress: event => onLoaded(event && event.loaded ? Math.min(event.loaded, blob.size) : 0)
      }).catch(err => {
        const status = err && err.response ? err.response.status : null
        // Coupure réseau ou erreur passagère du serveur : le même morceau repart une fois.
        if (attempt < 2 && (status === null || status >= 500)) {
          onLoaded(0)
          return this.uploadChunk(uploadId, index, blob, onLoaded, attempt + 1)
        }
        throw err
      })
    },
    setUploadProgress (sent, total) {
      if (this.uploadProgress === null || !total) {
        return
      }
      // 100 % est réservé à la réponse finale, le temps que l'API recolle le fichier.
      this.uploadProgress = Math.min(99, Math.round((sent * 100) / total))
    },
    submit () {
      if (!this.canSubmit || this.submitting) {
        return
      }
      this.submitting = true
      this.fieldErrors = {}
      this.generalErrors = []
      this.uploadProgress = this.inputType === 'audio' ? 0 : null

      const upload = this.inputType === 'audio' ? this.uploadInChunks(this.file) : Promise.resolve(null)
      upload.then(uploadId => {
        if (this.uploadProgress !== null) {
          this.uploadProgress = 100
        }
        return aiService.createMeeting(this.buildFormData({ uploadId }))
      }).then(meeting => {
        this.$buefy.notification.open({
          duration: 4000,
          message: 'Réunion déposée : le traitement démarre.',
          position: 'is-bottom-right',
          type: 'is-success',
          hasIcon: false
        })
        if (meeting && meeting.uuid) {
          this.$router.push({ name: 'app-meeting', params: { uuid: meeting.uuid } }).catch(() => {})
        } else {
          this.$router.push({ name: 'app-meetings' }).catch(() => {})
        }
      }).catch(err => {
        this.handleError(err)
      }).finally(() => {
        this.submitting = false
        this.uploadProgress = null
      })
    },
    handleError (err) {
      const status = err && err.response ? err.response.status : null
      if (status === 422) {
        const { fields, general } = validationErrors(err, KNOWN_FIELDS)
        this.fieldErrors = fields
        this.generalErrors = general.length || Object.keys(fields).length
          ? general
          : ['Le dépôt a été refusé, vérifiez les champs.']
        return
      }
      if (status === 403) {
        this.generalErrors = ['Vous n’avez pas le rôle requis pour déposer une réunion sur ce domaine.']
        return
      }
      if (status === 413) {
        this.generalErrors = ['L’enregistrement dépasse la taille maximale acceptée par le serveur.']
        return
      }
      const message = err && err.response && err.response.data && err.response.data.message
      this.generalErrors = [message
        ? `Le dépôt a échoué : ${message}`
        : 'Le dépôt a échoué. Vérifiez votre connexion puis réessayez.']
    },
    goBack () {
      this.$router.push({ name: 'app-meetings' }).catch(() => {})
    }
  }
}
</script>

<style src="./meetings.css"></style>

<style scoped>
.meeting-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 60rem;
  padding: 1.5rem;
}

.meeting-form__section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}

.meeting-form__modes {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

/* Cartes d'option (cf. choix du modèle dans la modale de campagne mail). */
.meeting-mode {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #ffffff;
  color: #111827;
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.meeting-mode:hover:not(:disabled) {
  border-color: #b4c5fe;
}

.meeting-mode--active {
  border-color: #2d5dfb;
  background: #f0f4ff;
  color: #090fbe;
}

.meeting-mode__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 1rem;
}

.meeting-mode--active .meeting-mode__icon {
  background: #dbeafe;
  color: #1d4ed8;
}

.meeting-mode__copy {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.meeting-mode__label {
  font-size: 0.875rem;
  font-weight: 600;
}

.meeting-mode__hint {
  color: #6b7280;
  font-size: 0.75rem;
}

.meeting-mode:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.meeting-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
}

.meeting-form__optional {
  color: #9ca3af;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
}

/* Champs texte (cf. objet de la modale de campagne mail). */
.meeting-input {
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: #ffffff;
  color: #111827;
  font-size: 0.875rem;
  line-height: 1.5;
}

.meeting-input::placeholder {
  color: #9ca3af;
}

.meeting-input:focus {
  border-color: #2d5dfb;
  box-shadow: 0 0 0 2px rgba(45, 93, 251, 0.35);
  outline: none;
}

.meeting-input--textarea {
  min-height: 14rem;
  resize: vertical;
}

.meeting-input--error {
  border-color: #fca5a5;
  background: #fef2f2;
}

.meeting-input:disabled {
  background: #f9fafb;
  cursor: not-allowed;
  opacity: 0.7;
}

.meeting-form__help {
  margin: 0;
  color: #6b7280;
  font-size: 0.75rem;
}

.meeting-form__help--warning {
  color: #b45309;
}

.meeting-form__error {
  margin: 0;
  color: #b91c1c;
  font-size: 0.8rem;
}

/* Zone de dépôt : même fond bleuté que les encadrés secondaires du suivi. */
.meeting-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  min-height: 12rem;
  padding: 1.5rem;
  border: 1px dashed #bfdbfe;
  border-radius: 0.65rem;
  background: #f8fbff;
  color: #4b5563;
  text-align: center;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.meeting-dropzone--dragging {
  border-color: #2563eb;
  background: #eff6ff;
}

.meeting-dropzone--filled {
  border-style: solid;
}

.meeting-dropzone--error {
  border-color: #fca5a5;
  background: #fef2f2;
}

.meeting-dropzone__icon {
  margin-bottom: 0.4rem;
  color: #93c5fd;
  font-size: 2rem;
}

.meeting-dropzone--filled .meeting-dropzone__icon {
  color: #2563eb;
}

.meeting-dropzone__name {
  max-width: 100%;
  margin: 0;
  color: #111827;
  font-size: 0.875rem;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.meeting-dropzone__hint {
  margin: 0;
  font-size: 0.8rem;
}

.meeting-dropzone__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.35rem 0 0;
  font-size: 0.8rem;
}

.meeting-form__alert {
  padding: 0.75rem 1rem;
  border: 1px solid #fecaca;
  border-radius: 0.65rem;
  background: #fef2f2;
  color: #991b1b;
  font-size: 0.875rem;
  line-height: 1.5;
}

.meeting-form__alert ul {
  margin: 0;
  padding-left: 1.1rem;
  list-style: disc;
}

.meeting-progress {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  color: #2563eb;
  font-size: 0.8rem;
}

.meeting-progress__bar {
  height: 0.5rem;
  border-radius: 9999px;
  background: #dbeafe;
  overflow: hidden;
}

.meeting-progress__value {
  height: 100%;
  border-radius: 9999px;
  background: #2563eb;
  transition: width 0.2s ease;
}

/* Pied de formulaire (cf. modale de campagne mail). */
.meeting-form__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

.meeting-form__cancel,
.meeting-form__submit {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  transition: background 0.15s ease;
}

.meeting-form__cancel {
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #475569;
}

.meeting-form__cancel:hover:not(:disabled) {
  background: #f1f5f9;
}

.meeting-form__submit {
  border: 0;
  background: #2563eb;
  color: #ffffff;
}

.meeting-form__submit:hover:not(:disabled) {
  background: #1d4ed8;
}

.meeting-form__cancel:disabled,
.meeting-form__submit:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.meeting-form__cancel:focus-visible,
.meeting-form__submit:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

@media (max-width: 768px) {
  .meeting-form {
    padding: 1.25rem;
  }

  .meeting-form__modes,
  .meeting-form__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
